import { ROUTE_DENIED } from 'constants/routerPath';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';

const defaultGuardRoleContextValue: {
	isAdmin: boolean;
	setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>;
	guardSelectorOptions: GuardSelectorOptions;
	setGuardSelectorOptions: React.Dispatch<
		React.SetStateAction<GuardSelectorOptions>
	>;
	realUserID: number;
} = {
	isAdmin: false,
	setIsAdmin: () => {},
	guardSelectorOptions: {
		selectedGuardID: null,
		selectedGuardName: '',
		selectedDate: null,
	},
	setGuardSelectorOptions: () => {},
	realUserID: 0,
};

const GuardRoleContext = createContext(defaultGuardRoleContextValue);

export const useGuardRoleContext = () => useContext(GuardRoleContext);

interface GuardRoleProviderProps {
	children: React.ReactNode;
}
interface GuardSelectorOptions {
	selectedGuardID: number | null;
	selectedGuardName: string;
	selectedDate: Date | null;
}

const adminRoles = ['can_dispatch_guard_v3_admin'];
const allowedRoles = [...adminRoles, 'can_dispatch_guard_v3_user'];
export const GuardRoleProvider = ({ children }: GuardRoleProviderProps) => {
	const { userInfo } = useSelector(selectUserInfo); // USE THIS TO SET ADMIN STATUS

	const [isAdmin, setIsAdmin] = useState(false); // Initialize isAdmin
	const [realUserID, setRealUserID] = useState<number>(0); // Initialize realUserID. This is the contactId of the logged in user.

	const navigate = useNavigate();

	const [guardSelectorOptions, setGuardSelectorOptions] =
		useState<GuardSelectorOptions>({
			selectedGuardID: null,
			selectedGuardName: '',
			selectedDate: null,
		});

	useEffect(() => {
		if (!userInfo.contactId) return;
		// Check if user has access first:
		const isAllowed = userInfo.roles.some((role: any) =>
			allowedRoles.includes(role),
		);
		const _isAdmin = userInfo.roles.some((role: any) =>
			adminRoles.includes(role),
		);
		if (!isAllowed) {
			navigate(ROUTE_DENIED);
		}
		setRealUserID(parseInt(userInfo.contactId));
		setIsAdmin(_isAdmin);
	}, [navigate, userInfo]);

	const value = {
		isAdmin,
		realUserID,
		guardSelectorOptions,
		setIsAdmin,
		setGuardSelectorOptions,
	};

	return (
		<GuardRoleContext.Provider value={value}>
			{children}
		</GuardRoleContext.Provider>
	);
};
